<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import { pickProductSearchQuery } from '#shared/utils/productSearchQuery'
import Spinner from '~/components/ui/Spinner.vue'
import { buildFilteredPageTitle } from '~/composables/buildFilteredPageTitle'
import { parseTrustpilotEmbed } from '~/composables/parseTrustpilotEmbed'

const { model } = defineProps(['model'])
const route = useRoute()
const router = useRouter()
const currentRoute = router.currentRoute
const appConfig = useAppConfig()
const trustpilotEmbed = computed(() => parseTrustpilotEmbed(model.ctaTrustpilotEmbed?.value))

const { showFooterForm } = useLayoutFlags()
showFooterForm.value = true
const filterSliderOpen = ref(false)

const queryParams = computed(() => ({
  ...pickProductSearchQuery(route.query),
  ProductCategories: model.catalogType.value,
}))

const pageNumber = computed(() => {
  const page = Number(route.query.page)
  return Number.isNaN(page) || page < 1 ? 1 : page
})

const { data, status, refresh } = useAsyncData(
  () => `products-${JSON.stringify(queryParams.value)}`,
  () => $fetch('/api/products', { query: queryParams.value, method: 'GET' }),
)

const products = computed(() => data?.value?.products ?? [])
const totalCount = computed(() => data?.value?.totalCount ?? 0)

const errorMessage = computed(() => model.errorMessage?.value || 'The products could not be retrieved. Please try again.')
const errorButtonText = computed(() => model.errorButtonText?.value || 'Try again')

const totalCountText = computed(() => {
  const count = totalCount.value || 0
  const singular = model.resultText?.value
  const plural = model.resultTextPlural?.value
  return `${count} ${count === 1 ? singular : plural}`
})

const activeFilterOptions = computed(() => {
  const activeFilters = []
  if (data?.value?.filter) {
    for (let i = 0; i < data.value.filter.length; i++) {
      for (let j = 0; j < data.value.filter[i].options.length; j++) {
        activeFilters.push({
          categoryName: data.value.filter[i].categoryName,
          optionName: data.value.filter[i].options[j],
        })
      }
    }
  }

  return activeFilters
})

const requestedFilterValues = computed(() => {
  const categories = model.searchCategories?.expandedValue ?? []
  return categories.flatMap((category) => {
    const raw = route.query[category.categoryUrl?.value]
    return (Array.isArray(raw) ? raw : [raw])
      .filter(value => typeof value === 'string')
      .flatMap(value => value.split(','))
      .filter(Boolean)
  })
})

const pageTitle = computed(() => buildFilteredPageTitle({
  baseTitle: model.pageTitle?.value,
  requestedValues: requestedFilterValues.value,
  appliedOptions: activeFilterOptions.value,
  searchTerm: route.query.searchTerm,
}))

// Event Handler Section
function closeFilterSlider() {
  filterSliderOpen.value = false
}

async function updateQuery(updates) {
  await navigateTo({
    path: route.path,
    query: {
      ...route.query,
      page: undefined,
      ...updates,
    },
  })
}

async function removeFilter(categoryName, optionName) {
  const existing = route.query[categoryName]?.split(',') || []

  const updated = existing.filter(o => o !== optionName)
  await updateQuery({
    [categoryName]: updated.length ? updated.join(',') : undefined,
  })
}

async function removeAllFilters() {
  await navigateTo({
    path: route.path,
    query: {},
  })
}

async function addFilter(categoryName, optionName) {
  const existing = route.query[categoryName]?.split(',') || []

  if (!existing.includes(optionName)) {
    await updateQuery({
      [categoryName]: [...existing, optionName].join(','),
    })
  }
}

// Manage search term
const searchTerm = computed({
  get: () => route.query.searchTerm || '',
  set: async (val) => {
    await navigateTo({
      query: {
        ...route.query,
        page: undefined,
        searchTerm: val || undefined,
      },
    })
  },
})

const updateSearch = useDebounceFn((val) => {
  searchTerm.value = val
}, 500, { maxWait: 1000 })

function clearSearchTerm() {
  searchTerm.value = ''
}
</script>

<template>
  <div class="product-listing-page">
    <div class="container-fluid">
      <div class="w-full px-0 md:px-20">
        <div class="general-page__title prose">
          <h1 v-if="pageTitle">
            {{ pageTitle }}
          </h1>
        </div>
        <div class="flex flex-col lg:flex-row mb-16 justify-between">
          <div class="mr-8">
            <p class="prose text-med-deep-navy font-medium mb-4 lg:mb-0">
              {{ model.ctaSectionMessage?.value }}
            </p>
          </div>
          <div class="flex flex-col gap-4">
            <div class="plp__cta-buttons flex flex-col md:flex-row gap-4">
              <UButton
                v-if="model.ctaButton1Link?.value"
                color="warning"
                variant="solid"
                :to="useCmsUrl(model.ctaButton1Link.value)"
                :locale="false"
                class="btn justify-center"
              >
                {{ model.ctaButton1Text?.value }}
              </UButton>
              <UButton
                v-if="model.ctaButton2Link?.value"
                color="neutral"
                variant="solid"
                :to="useCmsUrl(model.ctaButton2Link.value)"
                :locale="false"
                class="btn justify-center"
              >
                {{ model.ctaButton2Text?.value }}
              </UButton>
            </div>
            <TrustpilotWidgetGate
              v-if="trustpilotEmbed?.businessUnitId"
              :widget-html="model.ctaTrustpilotEmbed?.value"
            >
              <CompanyTrustpilotSummary
                :business-unit-id="trustpilotEmbed.businessUnitId"
                variant="inline"
              />
            </TrustpilotWidgetGate>
          </div>
        </div>
      </div>
    </div>
    <div class="plp__quick-filters-container mb-10 md:mb-15">
      <div class="container-fluid">
        <div
          class="plp__quick-filters flex flex-col xl:flex-row gap-y-8 xl:gap-x-16 px-0 md:px-20"
        >
          <div class="prose w-full xl:basis-1/4">
            <h2 class="h3">
              {{ model.quickFilterSectionTitle?.value }}
            </h2>

            <p class="text-med-blue">
              {{ model.quickFilterSectionMessage?.value }}
            </p>
          </div>

          <div class="plp__filter-list flex-1">
            <QuickFilterBlock
              v-for="filter in model.quickFilters?.expandedValue"
              :key="filter.name"
              :model="filter"
            />
          </div>

          <div class="prose w-full xl:basis-1/4">
            <div class="h3 title">
              {{ model.quickFilterPromoTitle?.value }}
            </div>

            <div
              class="plp__filter-promo-content"
              v-html="model.quickFilterPromoContent?.value"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="plp__products block-spacing">
      <div class="plp__products-mobile-header xl:hidden container-fluid shadow-lg">
        <div class="px-0 md:px-20">
          <div class="prose">
            <h3>
              {{ model.productListingTitle?.value }}
            </h3>
          </div>
          <div class="w-full flex justify-between items-center">
            <span class="italic text-xl">
              {{ totalCountText }}
            </span>
            <div>
              <USlideover
                v-model:open="filterSliderOpen"
                :title="model.productListingTitle?.value"
                :close="false"
                :ui="{
                  content: 'plp__filter-slider',
                }"
              >
                <UButton
                  label="Filter"
                  variant="ghost"
                  :trailing-icon="appConfig.ui.icons.funnel"
                  block
                  class="bg-med-light-aqua shadow-md font-normal text-lg w-auto"
                >
                  <template #leading>
                    <div
                      v-if="activeFilterOptions?.length > 0"
                      class="relative flex items-center justify-center"
                    >
                      <span
                        class="inline-flex items-center justify-center bg-med-navy text-white rounded-full m-1 w-8 h-8"
                      >
                        {{ activeFilterOptions.length }}
                      </span>
                    </div>
                  </template>
                </UButton>
                <template #body>
                  <div class="flex justify-between items-center mb-4">
                    <span class="italic text-lg">
                      {{ totalCountText }}
                    </span>
                    <UButton
                      label="Close"
                      variant="ghost"
                      :trailing-icon="appConfig.ui.icons.close"
                      block
                      class="text-med-navy shadow-md font-normal text-lg w-auto"
                      @click="closeFilterSlider"
                    />
                  </div>
                  <ProductListingFilterSection
                    :search-term="searchTerm"
                    :model="model"
                    :data="data"
                    @remove-filter="removeFilter"
                    @add-filter="addFilter"
                    @clear-search="clearSearchTerm"
                    @update:search-term="updateSearch"
                  />
                </template>
              </USlideover>
            </div>
          </div>
        </div>
      </div>
      <div class="container-fluid">
        <div class="w-full px-0 md:px-20">
          <div class="prose hidden xl:block">
            <h3>
              {{ model.productListingTitle?.value }}
            </h3>
          </div>
          <div class="general-page__wrap">
            <nav
              class="subnav"
              aria-label="Product filters"
            >
              <div class="subnav_body hidden xl:block">
                <ProductListingFilterSection
                  :search-term="searchTerm"
                  :model="model"
                  :data="data"
                  @remove-filter="removeFilter"
                  @add-filter="addFilter"
                  @clear-search="clearSearchTerm"
                  @update:search-term="updateSearch"
                />
              </div>
            </nav>
            <div class="main-body overflow-y-auto min-h-100">
              <div>
                <div
                  v-if="totalCount && totalCount > 0"
                  class="italic text-xl mb-4 hidden xl:block"
                >
                  {{ totalCountText }}
                </div>
                <div
                  v-if="data?.filter?.length > 0"
                  class="flex flex-row flex-wrap mb-8"
                >
                  <div class="flex flex-wrap">
                    <SearchFilterOptionBlock
                      v-for="filterOption in activeFilterOptions"
                      :key="filterOption.optionName"
                      :option-name="filterOption.optionName"
                      :category-name="filterOption.categoryName"
                      @remove-filter="removeFilter"
                    />
                  </div>
                  <button
                    class="ml-2 text-med-dark-gray underline items-start min-w-25"
                    @click="removeAllFilters"
                  >
                    {{ model.clearFiltersText.value }}
                  </button>
                </div>
                <div class="relative">
                  <div
                    v-show="totalCount > 0"
                    class="plp__products-list"
                  >
                    <ProductListItem
                      v-for="product in products"
                      :key="product.id"
                      :model="product"
                      :base-url="`${currentRoute.path}`"
                    />
                  </div>
                  <div
                    v-if="status === 'pending'"
                    class="absolute inset-0 bg-white/40 backdrop-blur-sm flex items-start pt-40 justify-center z-5 rounded-2xl min-h-100"
                  >
                    <Spinner />
                  </div>
                </div>
                <PaginationBlock
                  v-if="totalCount"
                  :total="totalCount"
                  :items-per-page="20"
                  :current-page="pageNumber"
                />
              </div>
              <div
                v-if="status === 'error'"
                class="plp__no-results prose mt-0 xl:mt-24"
                role="alert"
              >
                <p>{{ errorMessage }}</p>
                <UButton
                  color="neutral"
                  variant="solid"
                  class="btn justify-center"
                  @click="refresh()"
                >
                  {{ errorButtonText }}
                </UButton>
              </div>
              <div
                v-else-if="totalCount === 0"
                class="plp__no-results prose mt-0 xl:mt-24"
                v-html="model.noResultsBody.value"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="plp__brands container-fluid block-spacing">
      <div class="px-0 md:px-20 2xl:px-40">
        <div class="prose">
          <h2 v-if="model.popularBrandsTitle?.value">
            {{ model.popularBrandsTitle.value }}
          </h2>
          <p>
            {{ model.popularBrandsMessage?.value }}
          </p>
        </div>
        <div class="plp__brands-list">
          <ImageLinkBlock
            v-for="brand in model.popularBrands?.expandedValue?.filter(x => x.label)"
            :key="brand.label"
            :model="brand"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  @use '@/assets/css/pages/general-page.scss';
@use '@/assets/css/pages/product-listing-page.scss';
</style>
